import { reducerPaths } from '@common/app/reducerPaths';
import { Avatar, Box, Button, CircularProgress, FormControl, FormLabel, MenuItem, Paper, Select, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import profileIllustration from '../../../components/assets/profile_illustration.svg';
import { setAlertDialog } from 'src/toolkit/alertDialog/alertDialogSlice';
import OpaqueModal from '@common/components/custom/Modals/OpaqueModal';
import { useUpdateSellerProfileMutation } from 'src/toolkit/seller/sellerApi';
import FieldErrorText from 'src/components/ui/FieldErrorText';
import { useLazyGetCitiesQuery, useLazyGetCountriesQuery, useLazyGetStatesQuery } from 'src/toolkit/geography/geographyApi';
import theme from 'src/theme';

const FormInputSelect = ({ name, showLabel, showError = true, label, formik, options }) => {
  return (
    <Grid item sx={{ width: "47%" }}>
      {showLabel ? (
        <FormLabel
          fontWeight={500}
          fontSize={14}
          mb={0.5}
          sx={{
            color: theme.palette.text.primary,
          }}
        >
          {label}
        </FormLabel>
      ) : null}
      <Select
        name={name}
        value={formik?.values?.[name]}
        onChange={formik?.handleChange}
        onBlur={formik?.handleBlur}
        displayEmpty
        sx={{
          width: "100%",
          "& .MuiSelect-select": {
            opacity: !formik?.values?.[name] ? "0.5" : "1"
          },
        }}
      >
        <MenuItem
          value={""}
          disabled
          sx={{
            display: "none",
          }}
        >
          {label}
        </MenuItem>
        {options?.map?.(option => {
          return (
            <MenuItem key={option?.value} value={option?.value}>
              {option?.label}
            </MenuItem>
          );
        })}
      </Select>

      {formik?.touched?.[name] && formik?.errors?.[name] ? (
        <FieldErrorText>
          {formik?.errors?.[name]}
        </FieldErrorText>
      ) : null}
    </Grid>
  );
};


function Profile() {

  const { t } = useTranslation("profile");

  const rootContainerRef = React.useRef(null);

  const initialValues = React.useMemo(() => {
    return {
      Seller_FName: '',
      Seller_LName: '',
      Seller_Email: '',
      Seller_Phone: '',
      Seller_PostalCode: "",
      Seller_Country: '',
      Seller_State: '',
      Seller_City: '',
      profile_image: ""
    };
  }, []);

  const theme = useTheme();

  const dispatch = useDispatch();

  const auth = useSelector((store) => store[reducerPaths.auth]);

  const [getCountriesApiHandler, getCountriesApiData] = useLazyGetCountriesQuery();
  const [getStatesApiHandler, getStatesApiData] = useLazyGetStatesQuery();
  const [getCitiesApiHandler, getCitiesApiData] = useLazyGetCitiesQuery();
  const [updateProfileApiHandler, updateProfileApiData] = useUpdateSellerProfileMutation();

  const [countries, setCountries] = React.useState([]);
  const [states, setStates] = React.useState([]);
  const [cities, setCities] = React.useState([]);

  // const handleCurrencyChange = (event) => {
  //   setCurrency(event.target.value);
  // };

  function stringAvatar(name) {
    if (!name) return { children: '' };
    if (name.includes(' '))
      return {
        children: `${name?.split(' ')[0][0]}${name?.split(' ')[1][0]}`,
      };
    else
      return {
        children: `${name?.split(' ')[0][0]}`,
      };
  }

  const validationSchema = Yup.object({
    Seller_FName: Yup.string(),
    Seller_LName: Yup.string(),
    Seller_Email: Yup.string().email(''),
    Seller_Phone: Yup.string(),
  });

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      // Handle form submission
      console.log(values);

      const formData = new FormData();

      for (let key in values) {
        if (key === "profile_image" && (typeof values[key] === "string" && values[key]?.includes(`http`)) || !(values[key])) {
          continue;
        }
        
        formData.append(key, values[key]);
      }

      const res = await updateProfileApiHandler({ payload: formData });

      if (res?.error || ("status" in (res?.data || {}) && !res?.data?.status)) {
        dispatch(
          setAlertDialog({
            open: true,
            title: t(`Error`),
            message: res?.data?.message || res?.error?.data?.message || res?.data?.error || res?.error?.data || t('An error occurred while updating profile.'),
          })
        )
      } else {
        dispatch(
          setAlertDialog({
            open: true,
            title: t(`Success`),
            message: t('Profile updated successfully. Refreshing the profile details in a moment.'),
          })
        )
      }
    },
  });

  React.useEffect(() => {
    (async () => {
      const res = await getCountriesApiHandler();

      if (res?.isError || ("status" in res?.data && !res?.data?.status)) {
        dispatch(
          setAlertDialog({
            open: true,
            title: t(`Error!`),
            message:
              res?.error?.data?.error ||
              res?.error?.data?.message ||
              res?.data?.message ||
              t(`Some error occured in fetching the countries!`),
          })
        );
        return;
      }

      setCountries(
        res?.data?.map?.(country => {
          return {
            value: String(country?.id),
            label: country?.name,
          };
        }) || []
      );
    })();
  }, [dispatch, t]);

  React.useEffect(() => {
    (async () => {
      if (formik?.values?.Seller_Country) {
        const res = await getStatesApiHandler({
          countryId: formik?.values?.Seller_Country,
        });

        if (res?.isError || ("status" in res?.data && !res?.data?.status)) {
          dispatch(
            setAlertDialog({
              open: true,
              title: t(`Error!`),
              message:
                res?.error?.data?.error ||
                res?.error?.data?.message ||
                res?.data?.message ||
                t(`Some error occured in fetching the states!`),
            })
          );
          return;
        }

        setStates(
          res?.data?.map?.(state => {
            return {
              value: String(state?.id),
              label: state?.name,
            };
          }) || []
        );
      }
    })();
  }, [formik?.values?.Seller_Country, dispatch, t]);

  React.useEffect(() => {
    (async () => {
      if (formik?.values?.Seller_State) {
        const res = await getCitiesApiHandler({
          stateId: formik?.values?.Seller_State,
        });

        if (res?.isError || ("status" in res?.data && !res?.data?.status)) {
          dispatch(
            setAlertDialog({
              open: true,
              title: t(`Error!`),
              message:
                res?.error?.data?.error ||
                res?.error?.data?.message ||
                res?.data?.message ||
                t(`Some error occured in fetching the cities!`),
            })
          );
          return;
        }

        setCities(
          res?.data?.map?.(city => {
            return {
              value: String(city?.id),
              label: city?.name,
            };
          }) || []
        );
      }
    })();
  }, [formik?.values?.Seller_State, dispatch, t]);

  React.useEffect(() => {
    if (auth?.userData) {
      formik.setValues({
        Seller_FName: formik?.values?.Seller_FName || auth?.userData?.Seller_FName,
        Seller_LName: formik?.values?.Seller_LName || auth?.userData?.Seller_LName,
        Seller_Email: formik?.values?.Seller_Email || auth?.userData?.Seller_Email,
        Seller_Phone: formik?.values?.Seller_Phone || auth?.userData?.Seller_Phone,
        Seller_Country: formik?.values?.Seller_Country || auth?.userData?.Seller_Country,
        Seller_State: formik?.values?.Seller_State || auth?.userData?.Seller_State,
        Seller_City: formik?.values?.Seller_City || auth?.userData?.Seller_City,
        Seller_PostalCode: formik?.values?.Seller_PostalCode || auth?.userData?.Seller_PostalCode,
        profile_image: formik?.values?.profile_image || auth?.userData?.profile_image,
      });
    }
  }, [auth?.userData]);

  console.log(`Profile.jsx`, formik?.values);


  return (
    <Box
      sx={{
        width: '100%',
        minHeight: { xs: 'auto', md: 'calc(100vh - 92px)' },
        position: 'relative',
      }}
      ref={rootContainerRef}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: { xs: '16px', sm: '20px', md: '24px' },
          p: { xs: 2, sm: 2.5, md: 3 },
          overflow: 'auto',
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
          <Grid size={12}>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'center', sm: 'center' },
                textAlign: { xs: 'center', sm: 'left' },
                marginBottom: { xs: '1.5rem', sm: '2rem' },
                gap: { xs: '20px', sm: '30px', md: '40px' },
              }}
            >
              <Avatar
                {...stringAvatar(`${auth?.userData?.Seller_FName || ''} ${auth?.userData?.Seller_LName || ''}`)}
                alt={`${auth?.userData?.Seller_FName || ''} ${auth?.userData?.Seller_LName || ''}`}
                src={auth?.userData?.profile_image || ""}
                sx={{
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  width: { xs: '120px', sm: '150px', md: '200px' },
                  height: { xs: '120px', sm: '150px', md: '200px' },
                  border: { xs: '3px solid #23262C', md: '4px solid #23262C' },
                }}
              />
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: '600',
                    color: theme.palette.text.primary,
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                  }}
                >
                  {`${auth?.userData?.Seller_FName || ''} ${auth?.userData?.Seller_LName || ''}`}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, lg: 5 }}>
            <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
              <Grid size={12}>
                <Box sx={{ width: '100%' }}>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 'medium',
                        mb: 1,
                        color: 'text.primary',
                      }}
                    >
                      {t("Profile picture")}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex' }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder={t("Upload profile image here")}
                      value={formik.values?.profile_image?.name || ''}
                      disabled
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '4px 0 0 4px',
                          height: 56,
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      component="label"
                      sx={{
                        backgroundColor: theme.palette.text.primary,
                        color: theme.palette.text.secondary,
                        textTransform: 'none',
                        borderRadius: '0 4px 4px 0',
                        padding: { xs: '0.5rem 1rem', sm: '0.625rem 1.25rem' },
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        height: 56,
                        minWidth: { xs: 100, sm: 120, md: 140 },
                        // whiteSpace: 'nowrap',
                        '&:hover': {
                          backgroundColor: theme.palette.primary.gradient1,
                        },
                      }}
                    >
                      {t("Browse")}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => {
                          console.log("");
                          const file = e.target.files[0];
                          if (file) {
                            formik.setValues(values => {
                              return {
                                ...values,
                                profile_image: file
                              }
                            });
                          }
                        }}
                      />
                    </Button>
                  </Box>
                </Box>
              </Grid>

              <Grid size={12}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 'medium',
                    mb: 1,
                    color: 'text.primary',
                  }}
                >
                  {t("Full Name")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder={t("First Name")}
                      value={formik.values.Seller_FName}
                      onChange={formik.handleChange}
                      name='Seller_FName'
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: 56,
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder={t("Last Name")}
                      value={formik.values.Seller_LName}
                      onChange={formik.handleChange}
                      name='Seller_LName'
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: 56,
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={t("Email Address")}
                      placeholder={t("Email Address")}
                      value={formik.values.Seller_Email}
                      onChange={formik.handleChange}
                      name='Seller_Email'
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: 56,
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={t("Phone Number")}
                      placeholder={t("Phone Number")}
                      value={formik.values.Seller_Phone}
                      onChange={formik.handleChange}
                      name='Seller_Phone'
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: 56,
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={t("Postal Code")}
                      placeholder={t("Postal Code")}
                      value={formik.values.Seller_PostalCode}
                      onChange={formik.handleChange}
                      name='Seller_PostalCode'
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: 56,
                        },
                      }}
                    />
                  </Grid>

                  <FormInputSelect
                    name="Seller_Country"
                    showLabel={true}
                    label={t("Select country")}
                    formik={formik}
                    options={countries}
                  />
                  <FormInputSelect
                    name="Seller_State"
                    showLabel={true}
                    label={t("Select State")}
                    formik={formik}
                    options={states}
                  />
                  <FormInputSelect
                    name="Seller_City"
                    showLabel={true}
                    label={t("Select City")}
                    formik={formik}
                    options={cities}
                  />
                </Grid>
              </Grid>

              <Button
                onClick={formik.handleSubmit}
                sx={{
                  backgroundColor: theme.palette.text.primary,
                  color: theme.palette.text.secondary,
                  textTransform: 'none',
                  borderRadius: '0 4px 4px 0',
                  padding: { xs: '0.5rem 1rem', sm: '0.625rem 1.25rem' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  height: 56,
                  minWidth: { xs: 100, sm: 120, md: 140 },
                  // whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.gradient1,
                  },
                }}
                disabled={updateProfileApiData?.isLoading}
              >
                {updateProfileApiData?.isLoading ? (
                  <CircularProgress size={22} />
                ) : (
                  t("Save")
                )}
              </Button>

            </Grid>
          </Grid>

          {!auth?.userData ? (
            <OpaqueModal
              isModalOpen={true}
              rootContainerRef={rootContainerRef}
            />
          ) : null}
        </Grid>
      </Paper>
    </Box>
  );
}

export default Profile;